import { useEffect, useState } from 'react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import {
  Entity,
  RELATION_MEMBER_OF,
  UserEntity,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';

export interface RepositoryOption {
  entity: Entity;
  displayName: string;
  fullName: string;
  system: string;
  owner: string;
  value: string; // The value to be stored in the form
}

interface GroupedRepositories {
  [system: string]: RepositoryOption[];
}

export const useRepositoryData = () => {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const [repositories, setRepositories] = useState<RepositoryOption[]>([]);
  const [groupedRepositories, setGroupedRepositories] =
    useState<GroupedRepositories>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchRepositories = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get user identity and ownership information
        const identity = await identityApi.getBackstageIdentity();
        if (!identity) {
          throw new Error('User identity not available');
        }

        const userRef = parseEntityRef(identity.userEntityRef);
        const user = (await catalogApi.getEntityByRef({
          kind: 'User',
          namespace: userRef.namespace ?? 'default',
          name: userRef.name,
        })) as UserEntity;

        if (!user) {
          throw new Error('User entity not found');
        }

        // Get user's groups from membership relations
        const groups = getEntityRelations(user, RELATION_MEMBER_OF).map(
          entityRef => stringifyEntityRef(entityRef),
        );

        // Create ownership filter array
        const ownershipRefs = [...groups, `user:${user.metadata.name}`];

        const { items } = await catalogApi.getEntities({
          filter: {
            kind: 'Repository',
            'relations.ownedBy': ownershipRefs,
          },
        });

        const repositoryOptions: RepositoryOption[] = items
          .filter(entity => entity.spec)
          .map(entity => {
            const spec = entity.spec as any;
            // Use spec.fullName.split('/')[1] for displayName, fallback to metadata.name
            const displayName = spec.fullName
              ? String(spec.fullName).split('/')[1]
              : entity.metadata.name;
            const owner = spec.owner || entity.metadata.namespace || 'unknown';
            // Prefer spec.fullName when available; otherwise fall back to owner/name
            const fullName =
              (spec.fullName && String(spec.fullName)) ||
              `${owner}/${entity.metadata.name}`;
            const system = spec.system || 'Unknown';

            return {
              entity,
              displayName,
              fullName,
              system,
              owner,
              // Store the stringified entityRef as the form value (e.g. component:namespace/name)
              value: stringifyEntityRef(entity),
            };
          })
          .sort((a, b) => a.displayName.localeCompare(b.displayName));

        // Group repositories by system
        const grouped = repositoryOptions.reduce((acc, repo) => {
          const system = repo.system;
          if (!acc[system]) {
            acc[system] = [];
          }
          acc[system].push(repo);
          return acc;
        }, {} as GroupedRepositories);

        // Sort repositories within each system
        Object.keys(grouped).forEach(system => {
          grouped[system].sort((a, b) =>
            a.displayName.localeCompare(b.displayName),
          );
        });

        setRepositories(repositoryOptions);
        setGroupedRepositories(grouped);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to fetch repositories',
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRepositories();
  }, [catalogApi, identityApi]);

  const searchRepositories = (searchTerm: string): RepositoryOption[] => {
    if (!searchTerm) return repositories;

    const lowercaseSearch = searchTerm.toLowerCase();
    return repositories.filter(repo => {
      const candidates = [
        repo.fullName,
        `${repo.owner}/${repo.displayName}`,
        repo.displayName,
        repo.system,
        repo.owner,
      ].map(s => (s || '').toLowerCase());

      return candidates.some(c => c.includes(lowercaseSearch));
    });
  };

  return {
    repositories,
    groupedRepositories,
    loading,
    error,
    searchRepositories,
  };
};
